import { useCallback, useMemo } from 'react';
import { usePaginatedRemoteList } from '../../../shared/data/usePaginatedRemoteList';
import {
  countBranchSupportTickets,
  countMyTickets,
  listBranchSupportTickets,
  listMyTickets,
  type SupportTicketRow,
} from '../services/supportTicketsRepo';
import { useModulePermission } from '../../session/hooks/useModulePermission';
import { usePlanEntitlements } from '../../providers/PlanEntitlementsProvider';
import { useSessionStore } from '../../session/store/useSessionStore';

type Query = { userId: string; branchId: string; includeBranch: boolean };

export function useSupportTicketsList(userId: string | null | undefined) {
  const { entitlements } = usePlanEntitlements();
  const isOwner = entitlements?.is_owner === true;
  const canViewBranchTickets = useModulePermission('Support', 'view_branch_tickets');
  const branchId = useSessionStore((s) => s.currentBranchId) ?? '';
  const includeBranch = isOwner || canViewBranchTickets;

  const query = useMemo<Query>(
    () => ({ userId: userId ?? '', branchId, includeBranch }),
    [userId, branchId, includeBranch]
  );

  const fetchPage = useCallback(
    async ({ limit, offset, query: q }: { limit: number; offset: number; query: Query }) => {
      if (!q.userId) return [] as SupportTicketRow[];
      if (q.includeBranch && q.branchId) {
        try {
          const branchRows = await listBranchSupportTickets(q.branchId, { limit, offset });
          return branchRows;
        } catch {
          // Fallback to own tickets if RPC missing / denied
          return listMyTickets(q.userId, { limit, offset });
        }
      }
      return listMyTickets(q.userId, { limit, offset });
    },
    []
  );

  const count = useCallback(async (q: Query) => {
    if (!q.userId) return 0;
    if (q.includeBranch && q.branchId) {
      try {
        return await countBranchSupportTickets(q.branchId);
      } catch {
        // Fallback to own count if RPC missing / denied
        return countMyTickets(q.userId);
      }
    }
    return countMyTickets(q.userId);
  }, []);

  const paging = usePaginatedRemoteList<SupportTicketRow, Query>({
    storageKey: 'support_tickets_page_size',
    criteriaKey: `${query.userId}:${query.branchId}:${query.includeBranch ? 'branch' : 'mine'}`,
    query,
    enabled: Boolean(userId),
    logLabel: 'SupportTickets',
    fetchPage,
    count,
  });

  return {
    tickets: paging.items,
    pageIndex: paging.pageIndex,
    pageSize: paging.pageSize,
    setPageSize: paging.setPageSize,
    hasMore: paging.hasMore,
    loading: paging.loading,
    totalCount: paging.totalCount,
    loadNextPage: paging.loadNextPage,
    loadPreviousPage: paging.loadPreviousPage,
    reload: paging.reloadFromStart,
    flatListRef: paging.flatListRef,
    scrollViewRef: paging.scrollViewRef,
    includeBranchTickets: includeBranch,
  };
}

export type SupportTicketsListFlow = ReturnType<typeof useSupportTicketsList>;
